'use client';

import React from 'react';
import { HttpStatusScreen } from '@/components/widget';
import { sanitizePublicText } from '@/lib/sanitizePublicText';

type Props = {
  children: React.ReactNode;
  /** When this value changes (e.g. `usePathname()`), clear a captured error without remounting children. */
  resetKey?: string;
};

type State = {
  error: Error | null;
};

/**
 * Client-side error boundary for subtrees (complements Next.js `error.tsx`).
 * Does not log sensitive data in production builds.
 */
export default class RuntimeErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidUpdate(prevProps: Props) {
    const { resetKey } = this.props;
    if (
      resetKey !== undefined &&
      prevProps.resetKey !== resetKey &&
      this.state.error !== null
    ) {
      this.setState({ error: null });
    }
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[RuntimeErrorBoundary]', error, errorInfo.componentStack);
    }
  }

  private reset = () => {
    this.setState({ error: null });
  };

  override render() {
    const { error } = this.state;
    if (error) {
      const devHint =
        process.env.NODE_ENV !== 'production' ? sanitizePublicText(error.message) : undefined;
      return (
        <HttpStatusScreen
          statusCode={500}
          technicalHint={devHint}
          onReset={this.reset}
          resetLabel="Try again"
        />
      );
    }
    return this.props.children;
  }
}
